import { GetProjectEstimatesQuery } from '@/gql/graphql';
import { Button, Modal } from '@payaca/ambrosia';
import { formatPrice, getMinorUnitSymbol } from '@payaca/helpers/financeHelper';
import { FC } from 'react';

type BillSavingsEstimate = NonNullable<
  GetProjectEstimatesQuery['projectById']['estimates'][number]['billSavingsEstimate']
>;

type RatePerKwh =
  BillSavingsEstimate['heatPumpCostScenarios'][number]['tariff']['ratePerKwh'];

const formatRatePerKwh = (rate: RatePerKwh): string => {
  const valueNumber = Number(rate.valueInMinor);
  const minorSymbol = getMinorUnitSymbol(rate.currency.code);
  if (minorSymbol !== null) {
    const formattedMinorUnits = new Intl.NumberFormat(navigator.language, {
      maximumFractionDigits: 2,
    }).format(valueNumber);
    return `${formattedMinorUnits}${minorSymbol}/kWh`;
  }
  const formatted = formatPrice(navigator.language)({
    priceInMinorCurrencyUnits: valueNumber,
    exponent: rate.currency.exponent,
    currencyCode: rate.currency.code,
  });
  return `${formatted}/kWh`;
};

type BillSavingsExplainedModalProps = {
  isOpen: boolean;
  onClose: () => void;
  billSavingsEstimate: BillSavingsEstimate;
};

const BillSavingsExplainedModal: FC<BillSavingsExplainedModalProps> = (
  props
) => {
  const { isOpen, onClose, billSavingsEstimate } = props;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Bill savings explained">
      <Modal.Body className="space-y-6">
        <section>
          <h4 className="mb-2">Tariffs</h4>
          <div className="divide-y divide-gray-200">
            {billSavingsEstimate.heatPumpCostScenarios.map(
              (scenario, index) => (
                <div
                  key={index}
                  className="flex items-start justify-between gap-4 py-3 pl-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{scenario.tariff.name}</p>
                    <p className="supporting-body text-gray-500">
                      {scenario.tariff.source}
                    </p>
                  </div>
                  <p className="whitespace-nowrap font-medium text-blue-900">
                    {formatRatePerKwh(scenario.tariff.ratePerKwh)}
                  </p>
                </div>
              )
            )}
          </div>
        </section>

        <section>
          <h4 className="mb-2">Specifications</h4>
          <div className="divide-y divide-gray-200">
            <div className="flex items-center justify-between gap-4 py-3 pl-3">
              <span>Heating output</span>
              <span className="font-medium text-blue-900">
                {billSavingsEstimate.heatingOutputKw.toFixed(1)}kW
              </span>
            </div>
            <div className="flex items-center justify-between gap-4 py-3 pl-3">
              <span>{`SCOP @ ${billSavingsEstimate.flowTemperatureCelsius}°C`}</span>
              <span className="font-medium text-blue-900">
                {Math.round(billSavingsEstimate.scop * 100)}%
              </span>
            </div>
            {billSavingsEstimate.cylinderCapacityLitres !== null && (
              <div className="flex items-center justify-between gap-4 py-3 pl-3">
                <span>Capacity</span>
                <span className="font-medium text-blue-900">
                  {billSavingsEstimate.cylinderCapacityLitres}L
                </span>
              </div>
            )}
          </div>
        </section>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button onClick={onClose}>Ok</Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Modal>
  );
};

export default BillSavingsExplainedModal;
